import { jsPDF } from "jspdf";
import JSZip from "jszip";

import {
  downloadFile,
  wait,
  getImage,
  cleanStringValue,
} from "@/app/core-module/util/coreHelper";
import {
  splineRegular,
  splineMedium,
  splineSemiBold,
} from "@/app/core-module/util/spline-font";
import { EnvSubscriberHelper } from "@/app/env_subscriber_helper";

const generateAttendanceSheetPdf = ({
  attendanceSheets,
  download = false,
  debug = false,
}) => {
  try {
    if (!attendanceSheets?.length) return;
    //region pdf default variables and constants
    const defaultFontSize = 10;
    const smallFontSize = 13;
    const lineHeight = Math.floor(defaultFontSize / 2);
    let currentY = lineHeight;
    let pageNumber = 1;
    const maxCharPerLine = 185;
    const pageWidth = 192;
    const pageHeight = 280;
    const center = pageWidth / 2;
    const start = 14;
    const end = pageWidth;
    //endregion

    //region jspdf doc init
    let doc = new jsPDF({
      orientation: "p",
      format: "a4",
      compress: true,
    });

    //region font setup
    doc.addFileToVFS("SplineSans-Regular-normal.ttf", splineRegular);
    doc.addFont(
      "SplineSans-Regular-normal.ttf",
      "SplineSans-Regular",
      "normal"
    );

    doc.addFileToVFS("SplineSans-Medium-normal.ttf", splineMedium);
    doc.addFont("SplineSans-Medium-normal.ttf", "SplineSans-Medium", "normal");

    doc.addFileToVFS("SplineSans-SemiBold-normal.ttf", splineSemiBold);
    doc.addFont(
      "SplineSans-SemiBold-normal.ttf",
      "SplineSans-SemiBold",
      "normal"
    );
    //endregion
    //endregion

    //region utility functions
    const setFontType = (type, weight) => {
      if (type === "semi-bold" || weight === 600) {
        doc.setFont("SplineSans-SemiBold", "normal");
      } else if (type === "medium" || weight === 500) {
        doc.setFont("SplineSans-Medium", "normal");
      } else {
        //type=regular
        //weight=400
        doc.setFont("SplineSans-Regular", "normal");
      }
    };

    const trimText = (value, maxLength, maxLines) => {
      return doc.splitTextToSize(value, maxLength).slice(0, maxLines);
    };

    const getImageInfo = (image, width, height, { fit } = {}) => {
      height = height ?? width;
      width = width ?? height;
      let imageInfo, imageHeight, imageWidth, imageAspectRatio;

      if (fit != null && image != null) {
        try {
          imageInfo = doc.getImageProperties(image);
        } catch (_) {}

        imageHeight = imageInfo?.height ?? height;
        imageWidth = imageInfo?.width ?? width;
        imageAspectRatio = imageHeight / imageWidth;

        if (fit === "WIDTH" && width != null) {
          height = width * imageAspectRatio;
        } else if (fit === "HEIGHT" && height != null) {
          width = height / imageAspectRatio;
        }
      }

      return {
        width,
        height,
        widthOg: imageWidth,
        heightOg: imageHeight,
        aspectRatio: imageAspectRatio,
        fileType: imageInfo?.fileType?.toLowerCase() ?? "jpeg",
      };
    };

    const addImage = (image, x, y, width, height, { fit } = {}) => {
      const imageInfo = getImageInfo(image, width, height, { fit });

      if (
        image != null &&
        imageInfo.fileType &&
        imageInfo.fileType !== "unknown"
      ) {
        try {
          doc.addImage(
            image,
            imageInfo.fileType,
            x,
            y,
            imageInfo.width,
            imageInfo.height
          );
        } catch (_) {}
      }

      return { x, y, ...imageInfo };
    };
    //endregion

    const printAttendance = (orders, date) => {
      currentY = lineHeight;
      // doc = new jsPDF({
      //   compressPdf: true,
      //   format: "a4",
      // });

      printHeader(orders);
      printHeaderBlock();

      printInfo(orders);
      printTableHeaders();
      let size = orders.employees.length;

      let tableFooterONNextPage = false;
      let start = 19; //max 19 rows in a page if length > 13
      let end = 14; //max 13 rows in a page if length === 13
      for (let item = 0; item < size; item++) {
        printRow(orders.employees[item], item);
        if (19 >= size && size >= 14) {
          // ||  46 >= size && size >= 43 || 73 >= size && size >= 70 || 100 >=size && size >=97
          tableFooterONNextPage = true;
        } else {
          while (start < size + 3) {
            // debugger;
            if (start >= size && size >= end) {
              tableFooterONNextPage = true;
            }
            start = start + 27;
            end = start - 3;
          }
        }
      }
      if (tableFooterONNextPage) {
        gotoNextPage();
      }
      printTableFooter(orders);
      printPageFooter();
      // saveDoc(date);
    };

    const printHeader = val => {
      addImage(
        getImage(val?.subscriberInfo?.user?.avatar, {
          params: `?r=${Math.random()}`,
        }),
        start,
        currentY - 2,
        45,
        20,
        { fit: "HEIGHT" }
      );
      doc.setFontSize(12);
      setFontType("medium");
      doc.setTextColor(40, 17, 102);

      const { refNumber, revision } =
        EnvSubscriberHelper.getEnvAttendanceRelatedValues();

      let headerY = currentY + 5;

      if (refNumber.length) {
        doc.text(refNumber, end, headerY, {
          align: "right",
        });

        headerY = currentY + 10.5;
      }

      if (revision.length) {
        doc.text(
          `${revision}, ` + (val.subscriberInfo.attendanceRevisionDate ?? ""),
          end,
          headerY,
          { align: "right" }
        );

        headerY = currentY + 15.5;
      }

      if (val?.batchNumber) {
        doc.text(`Batch no: ${val?.batchNumber}`, end, headerY, {
          align: "right",
        });
      }
    };

    const printHeaderBlock = () => {
      doc.setFillColor(40, 17, 102);
      gotoNextLine(5);
      doc.rect(start, currentY, pageWidth - 14, 13, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      setFontType("semi-bold");
      doc.text(
        "Training attendance Sheet".toUpperCase(),
        center + 10,
        currentY + 8,
        { align: "center" }
      );
    };

    const printInfo = val => {
      doc.setTextColor(40, 17, 102);
      doc.setFontSize(12);
      setFontType("medium");
      gotoNextLine(5);

      const colon1XAxis1 = start + 30;
      const value1XAxis1 = start + 32;
      const title2XAxis1 = start + 100;
      const colon2XAxis1 = start + 123;
      const value2XAxis1 = start + 125;

      const courseTitleSplit = trimText(
        cleanStringValue(val?.courseTitle),
        146,
        2
      );
      const courseTitleSplitYAxis =
        courseTitleSplit.length > 1 ? currentY - 4 : currentY;
      doc.text("Course Title ", start, courseTitleSplitYAxis);
      doc.text(":", colon1XAxis1, courseTitleSplitYAxis);
      doc.text(courseTitleSplit, value1XAxis1, courseTitleSplitYAxis);

      gotoNextLine(1.5);
      doc.text("Date", start, currentY);
      doc.text(":", colon1XAxis1, currentY);
      doc.text(val?.date ?? "", value1XAxis1, currentY);

      doc.text("Location", title2XAxis1, currentY);
      doc.text(":", colon2XAxis1, currentY);

      gotoNextLine(1.5);
      doc.text("Company", start, currentY);
      doc.text(":", colon1XAxis1, currentY);
      doc.text(
        trimText(cleanStringValue(val?.company), 60, 2),
        value1XAxis1,
        currentY
      );

      doc.text("Trainer", title2XAxis1, currentY);
      doc.text(":", colon2XAxis1, currentY);
      doc.text(
        trimText(cleanStringValue(val?.trainer), 53, 2),
        value2XAxis1,
        currentY
      );
    };

    const printTableHeaders = () => {
      gotoNextLine(2);
      doc.rect(start, currentY, pageWidth - 14, 13, "F");
      gotoNextLine(1.5);
      doc.setTextColor(255, 255, 255);
      doc.setDrawColor(147, 115, 255);

      doc.line(start, currentY - 7.5, start, currentY + 5.5);
      doc.line(end, currentY - 7.5, end, currentY + 5.5);
      doc.line(start + 15, currentY - 7.5, start + 15, currentY + 5.5);
      doc.line(start + 72, currentY - 7.5, start + 72, currentY + 5.5);
      doc.line(start + 105, currentY - 7.5, start + 105, currentY + 5.5);
      doc.line(start + 140, currentY - 7.5, start + 140, currentY + 5.5);

      doc.setFontSize(12);
      setFontType("semi-bold");
      doc.text("S No.", start + 2, currentY);
      doc.text("Name", start + 40, currentY);
      doc.text("Emp ID", start + 82, currentY);
      doc.text("Designation", start + 112, currentY);
      doc.text("Signature", start + 150, currentY);
    };

    const printRow = (item, index) => {
      gotoNextLine(2);
      doc.line(start, currentY - 5.5, start, currentY + 4.5);
      doc.line(end, currentY - 5.5, end, currentY + 4.5);
      doc.line(start + 15, currentY - 5.5, start + 15, currentY + 4.5);
      doc.line(start + 72, currentY - 5.5, start + 72, currentY + 4.5);
      doc.line(start + 105, currentY - 5.5, start + 105, currentY + 4.5);
      doc.line(start + 140, currentY - 5.5, start + 140, currentY + 4.5);
      doc.line(start, currentY + 4.5, end, currentY + 4.5);
      if (index !== 0) {
        doc.line(start, currentY - 5.5, end, currentY - 5.5);
      }

      doc.setTextColor(40, 17, 102);
      doc.setFontSize(10);
      setFontType("medium");
      doc.text(String(index + 1), start + 6, currentY + 1);

      const yAxis1 = currentY + 1;
      const yAxis2 = index > 0 ? currentY - 1 : currentY - 0.7;
      const empName = trimText(cleanStringValue(item.firstName), 53, 2);
      doc.text(empName, start + 17, empName.length < 2 ? yAxis1 : yAxis2);
      const empID = trimText(cleanStringValue(item.civilIdOrPassport), 29, 2);
      doc.text(empID, start + 74, empID.length < 2 ? yAxis1 : yAxis2);
      const empDesignation = trimText(
        cleanStringValue(item.designation),
        31,
        2
      );
      doc.text(
        empDesignation,
        start + 107,
        empDesignation.length < 2 ? yAxis1 : yAxis2
      );

      // doc.line(start + 17, yAxis1, start + 17 + 53, yAxis1);
      // doc.line(start + 74, yAxis1, start + 74 + 29, yAxis1);
      // doc.line(start + 107, yAxis1, start + 107 + 31, yAxis1);

      doc.line(start, currentY - 5.5, start, currentY + 4.5);
      doc.line(end, currentY - 5.5, end, currentY + 4.5);
    };

    const printTableFooter = val => {
      gotoNextLine(2);
      doc.setFontSize(10);
      setFontType("semi-bold");
      doc.text("Remarks :", start + 2, currentY);
      doc.text(trimText(val?.remarks ?? "", 80, 7), start + 22, currentY);

      doc.text("Trainer Signature / Stamp", start + 120, currentY);
      doc.line(start + 105, currentY + 3, end, currentY + 3);
      doc.line(start, currentY - 5.5, start, currentY + 35);
      doc.line(end, currentY - 5.5, end, currentY + 35);
      doc.line(start, currentY + 35, end, currentY + 35);
      doc.line(start, currentY - 5.5, end, currentY - 5.5);

      doc.line(start + 105, currentY - 5.5, start + 105, currentY + 35);
    };

    const printPageFooter = () => {
      const { orgLogos } = EnvSubscriberHelper.getEnvAttendanceRelatedValues();

      if (orgLogos.length) {
        const orgLogoPadding = 2;
        const orgLogoSize = (pageWidth - 28) / orgLogos.length;
        let orgLogoXAxis = 14;

        orgLogos.forEach((orgLogo, index) => {
          const { width } = addImage(
            orgLogo,
            orgLogoXAxis,
            pageHeight - orgLogoSize / 2,
            orgLogoSize,
            orgLogoSize,
            { fit: "WIDTH" }
          );

          orgLogoXAxis += (width || orgLogoSize) + orgLogoPadding;
        });
      }

      // blue color rectangle at the bottom of page
      doc.rect(0, pageHeight + 14, pageWidth + 20, 4, "F");
    };

    const gotoNextLine = numberOfLines => {
      if (numberOfLines == null) numberOfLines = 1;
      currentY += numberOfLines * lineHeight;
      if (currentY > pageHeight) gotoNextPage();
    };

    const gotoNextPage = () => {
      doc.addPage();
      pageNumber++;
      currentY = 2 * lineHeight;
      // doc.text("Page: " + pageNumber, pageWidth - 14, pageHeight);
      currentY = 3 * lineHeight;
    };

    attendanceSheets.forEach((attendanceSheet, index) => {
      printAttendance(attendanceSheet);

      // if (attendanceSheets.length > index + 1) {
      //     doc.addPage();
      //     lineHeight = Math.floor(normalFontSize / 2);
      //     currentY = lineHeight;
      // }
    });

    if (debug) {
      window.open(doc.output("bloburl"), "_blank");
      return true;
    } else {
      if (download) {
        if (attendanceSheets.length === 1) {
          return doc.save(`attendance-sheet-0-${Date.now()}`);
        } else {
          return doc.save(`attendance-sheets-${Date.now()}`);
        }
      } else {
        return doc.output("blob");
      }
    }
  } catch (e) {
    console.log(
      "attendanceSheetHelper.generateAttendanceSheetPdf.exception:",
      e.message
    );

    return false;
  }
};

export const bulkDownloadAttendanceSheets = async ({
  attendanceSheets,
  zip = false,
  debug = false,
}) => {
  try {
    if (!attendanceSheets?.length) return;

    await wait(100);

    if (zip && attendanceSheets.length > 1) {
      const zip = new JSZip();
      const sheets = zip.folder(`attendance-sheets-${Date.now()}`);

      attendanceSheets.forEach((attendanceSheet, index) => {
        const blobData = generateAttendanceSheetPdf({
          attendanceSheets: [attendanceSheet],
        });

        if (blobData) {
          sheets.file(`attendance-sheet-${index}-${Date.now()}.pdf`, blobData);
        }
      });

      if (sheets?.files && Object.keys(sheets?.files).length) {
        return await zip
          .generateAsync({ type: "blob" })
          .then(data => {
            downloadFile({
              data,
              fileName: `attendance-sheets-${Date.now()}.zip`,
            });
            return true;
          })
          .catch(e => {
            console.log(
              "attendanceSheetHelper.bulkDownloadAttendanceSheets:zip.generateAsync:error:",
              e.message
            );

            return false;
          });
      }
    } else {
      return !!generateAttendanceSheetPdf({
        attendanceSheets,
        download: true,
        debug,
      });
    }
  } catch (e) {
    console.log(
      "attendanceSheetHelper.bulkDownloadAttendanceSheets.exception:",
      e.message
    );

    return false;
  }
};
